import { createAction, createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { apiCallBegan } from "./api";
import { getCoverIdOfPrice } from "./businesslogic/hardwiredlogic";
import {
  addResponseSeqToCols,
  arrayToObject,
  convertResponsesArrayToObjects,
  coverTableToCoverTypeObject,
  coverTypeObjectToRows,
  PossibleCols,
  PossibleApiStatusValues,
  sequenceOfResponsesArray,
  PossibleRowTypesForViewCompareTable,
} from "./helperfuncs";

//#region Helper functions

const ordercoverTypeTableAsPerDefaultOrder = ({
  covertypetable,
  covertypes,
}) => {
  let newcovertypetable = {};

  for (let code in covertypes) {
    if (code in covertypetable) {
      newcovertypetable[code] = covertypetable[code];
    }
  }

  return newcovertypetable;
};

//#endregion

const slice = createSlice({
  name: "compareview",
  initialState: {
    prospectid: null,
    responseid: null, // only when viewquotes is clicked
    dataLoading: PossibleApiStatusValues.notstarted,
    data: null,
    covertypetable: null,
    rows: [],
    cols: [],
    expand: {},
    insurerModalInfo: {},
    expandLocation: {},
    covers: null,
    covertypes: null,
    clientneeds: null,
    responses: null,
    insurers: null,
    responseSequence: [],
    locationAddresses: {},
    clientNeedsEdit: false,
  },
  reducers: {
    // action => action handler

    storeProspectidForCompareView: (compareview, action) => {
      compareview.prospectid = action.payload;
    },
    storeResponseIdForCompareView: (compareview, action) => {
      compareview.responseid = action.payload;
    },
    editClientNeeds: (compareview, action) => {
      compareview.clientNeedsEdit = !compareview.clientNeedsEdit;
    },
    updateClientNeed: (compareview, action) => {
      compareview.clientneeds = {
        ...compareview.clientneeds,
        ...action.payload,
      };
    },
    showInsurerInfoModal: (compareview, action) => {
      compareview.insurerModalInfo = {
        toggleInsurerInfoModel: true,
        ...action.payload,
      };
    },
    closeInsurerInfoModal: (compareview, action) => {
      compareview.insurerModalInfo = { toggleInsurerInfoModel: false };
    },
    changeExpandProp: (compareview, action) => {
      compareview.expand[action.payload.covertypecode].expand =
        action.payload.changedvalue;
    },
    changeLocationExpandProp: (compareview, action) => {
      const { locationid, buildingid, changedvalue } = action.payload;

      if (!compareview.expandLocation[locationid]) {
        compareview.expandLocation[locationid] = {};
      }

      if (buildingid != null) {
        if (!compareview.expandLocation[locationid][buildingid]) {
          compareview.expandLocation[locationid][buildingid] = {};
        }

        compareview.expandLocation[locationid][buildingid].expand =
          changedvalue;
      } else {
        compareview.expandLocation[locationid].expand = changedvalue;
      }
    },
    gettingCompareViewDataInitialized: (compareview, action) => {
      compareview.dataLoading = PossibleApiStatusValues.loading;
    },
    gettingCompareViewDataFailed: (compareview, action) => {
      compareview.dataLoading = PossibleApiStatusValues.failed;
    },
    gettingCompareViewCoverMatchesSuccess: (compareview, action) => {
      var responses = action.payload;
      for (var insurerCode in compareview.responses) {
        let insurerResponse = responses.find(
          (x) => x?.insurercode == insurerCode
        );
        if (insurerResponse) {
          compareview.responses[insurerCode].covermatches =
            insurerResponse.covermatches;
        }
      }
    },
    gettingCompareViewDataSuccess: (compareview, action) => {
      compareview.dataLoading = PossibleApiStatusValues.success;
      compareview.data = action.payload;

      // TODO:  delete LOCATION and add LOCATION_1, LOCATION_2 as per number of locations in each response

      // endTODO

      //#region This Part is only required by Add-Edit page.
      //        In future this will be responsibly handled for
      //        add-edit page seperately. TODO: Remove this
      //        logic after MVP with a proper design.

      let coversdata = action.payload.covers;
      // This logic for covers child parent relationship.

      let updatedRows = [];
      const rowsWithParentIds = coversdata.filter((r) => r.parentid !== null);
      const rowsWithoutParentIds = coversdata.filter(
        (r) => r.parentid === null
      );
      for (let i = 0; i < coversdata.length; i++) {
        if (coversdata[i].parentid === undefined)
          updatedRows.push(coversdata[i]);
        if (coversdata[i].parentid === null) {
          updatedRows.push(coversdata[i]);
          const filteredData = coversdata
            .filter((r) => r.parentid === coversdata[i].id)
            .sort((a, b) => a.id - b.id); // b - a for reverse sort
          updatedRows = [...updatedRows, ...filteredData];
        }
      }

      if (action.payload.responses[0].covers) {
        for (let covid in action.payload.responses[0].covers) {
          for (let cov of updatedRows) {
            if (cov.id == covid && !cov.covervalue) {
              cov.covervalue = action.payload.responses[0].covers[covid];
            }
          }
        }
      }
      let covertypetable = coverTableToCoverTypeObject({
        covers: updatedRows,
      });

      //#endregion
      compareview.covers = arrayToObject(action.payload.covers, "id");
      compareview.locationAddresses = action.payload.locationAddresses;
      let covertypes_withoutLocations = arrayToObject(
        action.payload.covertypes,
        "code"
      );
      compareview.covertypes = { ...covertypes_withoutLocations };
      //order covertypes as per defaultorder
      compareview.covertypetable = ordercoverTypeTableAsPerDefaultOrder({
        covertypetable,
        covertypes: compareview.covertypes,
      });

      compareview.insurers = arrayToObject(action.payload.insurers, "code");
      compareview.clientneeds = action.payload.clientneeds;
      compareview.responses = convertResponsesArrayToObjects({
        responses: action.payload.responses,
      });
      let responseSequence = sequenceOfResponsesArray({
        responses: action.payload.responses,
      });
      compareview.responseSequence = responseSequence;

      // convert covertype table to rows
      let { rows, covertypeexpandprop, locationExpandProp } =
        coverTypeObjectToRows({
          covertypeobject: compareview.covertypetable,
          locationAddresses: compareview.locationAddresses,
        });

      //#region This Part is only required by Add-Edit page.
      //        In future this will be responsibly handled for
      //        add-edit page seperately. TODO: Remove this
      //        logic after MVP with a proper design.
      rows.forEach((row) => {
        if (row.locationid) {
          for (const locid in action.payload.responses[0].locations) {
            if (locid == row.locationid) {
              for (const key in action.payload.responses[0].locations[locid]) {
                if (key == row.id) {
                  row.covervalue =
                    action.payload.responses[0].locations[locid][key];
                }
              }
            }
          }
        }
      });
      //#endregion
      compareview.rows = [...rows];

      let cols = [
        {
          type: PossibleCols.COVERINFO,
          heading: { en: "Cover Name", fr: "Couverature" },
        },
        {
          type: PossibleCols.CLIENTNEEDS,
          heading: { en: "Client Needs", fr: "Besoins du client" },
        },
      ];

      compareview.cols = addResponseSeqToCols({ cols, responseSequence });

      compareview.expand = covertypeexpandprop;
      compareview.expandLocation = locationExpandProp;
    },
    updateRows: (compareview, action) => {
      compareview.rows = action.payload.rows;
    },
  },
});

export const {
  storeProspectidForCompareView,
  storeResponseIdForCompareView,
  gettingCompareViewDataFailed,
  gettingCompareViewDataInitialized,
  gettingCompareViewDataSuccess,
  gettingCompareViewCoverMatchesSuccess,
  changeExpandProp,
  editClientNeeds,
  updateClientNeed,
  showInsurerInfoModal,
  closeInsurerInfoModal,
  changeLocationExpandProp,
  updateRows,
} = slice.actions;

export default slice.reducer;

//#region Action Creators and API callings

export const getCompareViewData = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: `/api/responses/getresponsesforcompareview${
      data.isProspectIdEncrypted ? "-open" : ""
    }`,
    method: "post",
    data: data,
    onStart: gettingCompareViewDataInitialized.type,
    onSuccess: gettingCompareViewDataSuccess.type,
    onError: gettingCompareViewDataFailed.type,
  });

export const updateClientNeeds = () => (dispatch, getState) => {
  let compareview = getState().entities.compareview;
  dispatch(
    apiCallBegan({
      serviceUrl: process.env.REACT_APP_BASICSERVICE,
      url: `/api/prospects/updateprospect`,
      method: "post",
      data: {
        prospectid: compareview.prospectid,
        clientneeds: compareview.clientneeds,
      },
    })
  );

  dispatch(
    apiCallBegan({
      serviceUrl: process.env.REACT_APP_BASICSERVICE,
      url: `/api/iic/updateCoverMatches`,
      method: "post",
      data: {
        prospectid: compareview.prospectid,
        clientneeds: compareview.clientneeds,
      },
      onSuccess: gettingCompareViewCoverMatchesSuccess.type,
    })
  );
};

//#endregion

//#region Selectors

export const getclientNeedsEdit = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.clientNeedsEdit
);

export const getPossibleApiStatusValueFromCompareView = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.dataLoading
);

export const getProspectidFromCompareView = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.prospectid
);

export const getResponseIdFromCompareView = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.responseid
);

export const getCompareViewRows = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.rows
);

export const getInsurerModalInfo = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.insurerModalInfo
);

export const getCompareViewExpand = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.expand
);

export const getCompareViewExpandLoc = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.expandLocation
);

export const getCols = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.cols
);

export const getCompareViewStateData = createSelector(
  (state) => state.entities.compareview,
  (compareview) => compareview.data
);

export const getExpandableBoolean =
  ({ covertypecode }) =>
  (state) => {
    if (state.entities.compareview.expand) {
      if (state.entities.compareview.expand[covertypecode]) {
        return state.entities.compareview.expand[covertypecode].expand;
      }
    }

    return false;
  };

export const getQuoteyScore =
  ({ insurercode }) =>
  (state) => {
    try {
      return state.entities.compareview.responses[insurercode].quoteyscore;
    } catch (e) {
      return null;
    }
  };

export const getToShowGreenBarForQouteyScore =
  ({ insurercode }) =>
  (state) => {
    try {
      let rs = state.entities.compareview.responseSequence;
      let arrayForMax = [];
      rs.forEach((rid) => {
        let quoteyScore =
          state.entities.compareview.responses[rid]?.quoteyscore;
        if (!isNaN(quoteyScore)) {
          arrayForMax.push({
            responseid: rid,
            value: state.entities.compareview.responses[rid]?.quoteyscore,
          });
        }
      });

      let maxValueObject = arrayForMax.reduce(function (prev, current) {
        return prev.value > current.value ? prev : current;
      });

      return insurercode === maxValueObject.responseid;
    } catch (e) {
      return false;
    }
  };

export const getExpandableBooleanForLocation =
  ({ locationid, buildingid }) =>
  (state) => {
    const expandLocation = state.entities.compareview.expandLocation;

    if (expandLocation && expandLocation[locationid]) {
      if (buildingid != null && expandLocation[locationid][buildingid]) {
        return expandLocation[locationid][buildingid].expand;
      } else {
        return expandLocation[locationid].expand || false;
      }
    }

    return false;
  };

export const getResponseDetailsFromInsurerCodeandCoverId =
  ({ insurercode, coverid }) =>
  (state) => {
    if (
      state.entities.compareview.responses[insurercode] &&
      state.entities.compareview.responses[insurercode].covers
    ) {
      if (coverid in state.entities.compareview.responses[insurercode].covers) {
        return state.entities.compareview.responses[insurercode].covers[
          coverid
        ];
      }
    }

    return null;
  };

export const getResponseDetailsFromInsurerCodesandCoverId =
  ({ insurercodes, coverid }) =>
  (state) => {
    var returnArr = [];
    for (let insurercode of insurercodes) {
      if (
        state.entities.compareview.responses[insurercode] &&
        state.entities.compareview.responses[insurercode].covers
      ) {
        if (
          coverid in state.entities.compareview.responses[insurercode].covers
        ) {
          returnArr.push(
            state.entities.compareview.responses[insurercode].covers[coverid]
          );
        }
      }
    }
    return returnArr;
  };

export const getCompareViewResponseDetails = () => (state) => {
  return state.entities.compareview.responses;
};

export const getResponseDetailsFromInsurerCodeandCoverIdAndLocationId =
  ({ insurercode, coverid, locationid, buildingid }) =>
  (state) => {
    const insurerResponse = state.entities.compareview.responses[insurercode];

    if (insurerResponse && insurerResponse.locations) {
      const locationData = insurerResponse.locations[locationid];

      if (locationData) {
        // Check if buildingid is provided and valid
        if (
          buildingid !== undefined &&
          locationData.buildings &&
          locationData.buildings[buildingid] &&
          locationData.buildings[buildingid][coverid]
        ) {
          // Get data for specific building
          return locationData.buildings[buildingid][coverid] || null;
        } else {
          // Backward compatibility for older structure without buildings
          if (coverid in locationData) {
            return locationData[coverid];
          }
        }
      }
    }

    return null;
  };

export const getResponseDetailsFromInsurerCodesCoverIdAndLocationId =
  ({ insurercodes, coverid, locationid, buildingid }) =>
  (state) => {
    var returnArr = [];
    for (let insurercode of insurercodes) {
      const insurerResponse = state.entities.compareview.responses[insurercode];

      if (insurerResponse && insurerResponse.locations) {
        const locationData = insurerResponse.locations[locationid];

        if (locationData) {
          // Check if buildingid is provided and valid
          if (
            buildingid !== undefined &&
            locationData.buildings &&
            locationData.buildings[buildingid] &&
            locationData.buildings[buildingid][coverid]
          ) {
            // Get data for specific building
            returnArr.push(locationData.buildings[buildingid][coverid]);
          } else {
            // Backward compatibility for older structure without buildings
            if (coverid in locationData) {
              returnArr.push(locationData[coverid]);
            }
          }
        }
      }
    }
    return returnArr;
  };

export const getCovermatchDetailsFromInsurerCodeandCoverIdAndLocationId =
  ({ insurercode, coverid, locationid, buildingid }) =>
  (state) => {
    const insurerResponse = state.entities.compareview.responses[insurercode];

    if (insurerResponse && insurerResponse.locations) {
      const locationData = insurerResponse.locations[locationid];

      if (locationData) {
        // Check if buildingid is provided and valid
        if (coverid == 12) {
          //console.log("locationData");
          //console.log(locationData);
        }
        if (
          buildingid !== undefined &&
          locationData.buildings &&
          locationData.buildings[buildingid]
          // temp fix until buildings has its own cover mataches
        ) {
          if (Array.isArray(locationData.covermatches[coverid])) {
            return locationData.covermatches[coverid][buildingid];
          }
          // Get data for specific building
          if (
            "covermatches" in locationData.buildings[buildingid] &&
            coverid in locationData.covermatches
          ) {
            //currently covermataches work for all, so this is not needed until its case by case.
            // return locationData.buildings[buildingid].covermatches[coverid];
            return locationData.covermatches[coverid];
          } else if (buildingid == 0 && locationData?.covermatches?.[coverid]) {
            return locationData.covermatches[coverid];
          }
        } else {
          // Backward compatibility for older structure without buildings
          if (
            "covermatches" in locationData &&
            coverid in locationData.covermatches
          ) {
            return locationData.covermatches[coverid];
          }
        }
      }
    }

    return null;
  };

export const getCovermatchDetailsFromInsurerCodeandCoverId =
  ({ insurercode, coverid }) =>
  (state) => {
    if (
      state.entities.compareview.responses[insurercode] &&
      state.entities.compareview.responses[insurercode].covermatches
    ) {
      if (
        coverid in
        state.entities.compareview.responses[insurercode].covermatches
      ) {
        return state.entities.compareview.responses[insurercode].covermatches[
          coverid
        ];
      }
    }

    return null;
  };

export const getCoverInfoFromCoverId =
  ({ coverid }) =>
  (state) =>
    state.entities.compareview.covers[coverid];

export const getCoverTypeInfoFromCoverId =
  ({ covertypecode }) =>
  (state) =>
    state.entities.compareview.covertypes[covertypecode];

export const getClientNeedsFromCoverId =
  ({ coverid }) =>
  (state) => {
    if (state.entities.compareview.clientneeds) {
      if (coverid in state.entities.compareview.clientneeds)
        return state.entities.compareview.clientneeds[coverid];
    }

    return null;
  };

export const getAllClientNeeds = () => (state) => {
  if (state.entities.compareview.clientneeds) {
    return state.entities.compareview.clientneeds;
  }
  return null;
};

export const getInsuerInforUsingCode =
  ({ insurercode }) =>
  (state) => {
    if (state.entities.compareview.insurers) {
      if (insurercode in state.entities.compareview.insurers)
        return state.entities.compareview.insurers[insurercode];
    }

    return null;
  };

//#endregion
